import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Building2,
  FileCheck,
  BookOpen,
  List,
  Info,
  ExternalLink,
  RefreshCw,
  Download,
  ChevronRight,
} from "lucide-react";

interface ComplianceCheck {
  id: string;
  standard: string;
  title: string;
  description: string;
  status: "compliant" | "warning" | "non_compliant" | "not_applicable";
  details?: string;
  recommendation?: string;
}

interface StandardCategory {
  name: string;
  description: string;
  checks: ComplianceCheck[];
}

export default function SwissGAAPFER() {
  const { selectedCompanyId } = useCompany();
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  // Query for company data
  const { data: company, isLoading: isLoadingCompany } =
    trpc.company.get.useQuery(
      { id: selectedCompanyId! },
      { enabled: !!selectedCompanyId }
    );

  // Mock compliance data - in production this would come from actual analysis
  const complianceCategories: StandardCategory[] = [
    {
      name: "Framework Standards (FER 1-6)",
      description: "Core standards for presentation and disclosure",
      checks: [
        {
          id: "fer1",
          standard: "FER 1",
          title: "Principles of Presentation",
          description:
            "True and fair view, going concern, consistency, and prudence",
          status: "compliant",
          details: "Financial statements reflect true economic position",
        },
        {
          id: "fer2",
          standard: "FER 2",
          title: "Valuation",
          description: "Assets and liabilities valued consistently",
          status: "compliant",
          details: "Valuation methods documented and applied consistently",
        },
        {
          id: "fer3",
          standard: "FER 3",
          title: "Presentation and Structure",
          description: "Balance sheet and income statement structure",
          status: "warning",
          details: "Some line items may need reclassification",
          recommendation:
            "Review classification of operating vs non-operating items",
        },
        {
          id: "fer4",
          standard: "FER 4",
          title: "Cash Flow Statement",
          description: "Direct or indirect method cash flow presentation",
          status: "compliant",
          details: "Cash flow statement prepared using indirect method",
        },
        {
          id: "fer5",
          standard: "FER 5",
          title: "Off-Balance Sheet Transactions",
          description: "Disclosure of contingent liabilities and commitments",
          status: "warning",
          details: "Some lease commitments may need additional disclosure",
          recommendation: "Document all operating lease obligations",
        },
        {
          id: "fer6",
          standard: "FER 6",
          title: "Notes to Financial Statements",
          description: "Required disclosures and explanatory notes",
          status: "compliant",
          details: "All required notes present and complete",
        },
      ],
    },
    {
      name: "Specific Standards (FER 10-27)",
      description: "Standards for specific accounting topics",
      checks: [
        {
          id: "fer10",
          standard: "FER 10",
          title: "Intangible Assets",
          description: "Recognition and amortization of intangible assets",
          status: "compliant",
          details: "Intangible assets properly capitalized and amortized",
        },
        {
          id: "fer11",
          standard: "FER 11",
          title: "Taxes",
          description: "Income tax accounting and deferred taxes",
          status: "compliant",
          details: "Tax provisions calculated correctly",
        },
        {
          id: "fer13",
          standard: "FER 13",
          title: "Leasing",
          description: "Treatment of operating and finance leases",
          status: "not_applicable",
          details: "No significant lease agreements identified",
        },
        {
          id: "fer15",
          standard: "FER 15",
          title: "Related Party Transactions",
          description: "Disclosure of transactions with related parties",
          status: "non_compliant",
          details: "Related party disclosures incomplete",
          recommendation:
            "Identify and document all related party transactions including amounts",
        },
        {
          id: "fer16",
          standard: "FER 16",
          title: "Pension Obligations",
          description: "Employee benefit obligations accounting",
          status: "warning",
          details:
            "BVG pension fund contributions recorded but actuarial adjustments pending",
          recommendation: "Obtain actuarial valuation for pension obligations",
        },
        {
          id: "fer18",
          standard: "FER 18",
          title: "Share-Based Payments",
          description: "Accounting for stock options and similar instruments",
          status: "not_applicable",
          details: "No share-based payment arrangements",
        },
        {
          id: "fer20",
          standard: "FER 20",
          title: "Impairment",
          description: "Assessment and recognition of asset impairments",
          status: "compliant",
          details: "Annual impairment review conducted",
        },
        {
          id: "fer23",
          standard: "FER 23",
          title: "Provisions",
          description: "Recognition and measurement of provisions",
          status: "compliant",
          details: "Provisions properly recognized and disclosed",
        },
        {
          id: "fer27",
          standard: "FER 27",
          title: "Derivative Financial Instruments",
          description: "Accounting for hedging and speculative derivatives",
          status: "not_applicable",
          details: "No derivative instruments held",
        },
      ],
    },
    {
      name: "SME Standards (Swiss GAAP FER 21)",
      description: "Simplified standards for small and medium enterprises",
      checks: [
        {
          id: "fer21_revenue",
          standard: "FER 21",
          title: "Revenue Recognition",
          description: "Timing and measurement of revenue",
          status: "compliant",
          details:
            "Revenue recognized when goods delivered or services performed",
        },
        {
          id: "fer21_inventory",
          standard: "FER 21",
          title: "Inventory Valuation",
          description: "Cost or net realizable value, whichever is lower",
          status: "compliant",
          details: "Inventory valued at FIFO cost",
        },
        {
          id: "fer21_depreciation",
          standard: "FER 21",
          title: "Depreciation",
          description: "Systematic depreciation of fixed assets",
          status: "compliant",
          details: "Standard useful lives applied consistently",
        },
      ],
    },
  ];

  const getStatusCounts = () => {
    let compliant = 0;
    let warning = 0;
    let nonCompliant = 0;
    let notApplicable = 0;

    complianceCategories.forEach(cat => {
      cat.checks.forEach(check => {
        switch (check.status) {
          case "compliant":
            compliant++;
            break;
          case "warning":
            warning++;
            break;
          case "non_compliant":
            nonCompliant++;
            break;
          case "not_applicable":
            notApplicable++;
            break;
        }
      });
    });

    return {
      compliant,
      warning,
      nonCompliant,
      notApplicable,
      total: compliant + warning + nonCompliant,
    };
  };

  const counts = getStatusCounts();
  const overallScore = Math.round((counts.compliant / counts.total) * 100);

  const runComplianceCheck = async () => {
    setIsChecking(true);
    // Simulate analysis
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsChecking(false);
    setLastChecked(new Date());
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">No Company Selected</h2>
            <p className="text-muted-foreground">
              Please select a company to check Swiss GAAP FER compliance.
            </p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoadingCompany) {
    return (
      <DashboardLayout>
        <div className="container py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FileCheck className="h-8 w-8 text-primary" />
                Swiss GAAP FER Compliance
              </h1>
              <p className="text-muted-foreground mt-2">
                Verify compliance with Swiss accounting standards for{" "}
                {company?.name}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {lastChecked && (
                <p className="text-sm text-muted-foreground">
                  Last checked: {lastChecked.toLocaleString()}
                </p>
              )}
              <Button
                variant="outline"
                onClick={runComplianceCheck}
                disabled={isChecking}
              >
                {isChecking ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Run Check
              </Button>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-success/20">
                    <CheckCircle className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{counts.compliant}</p>
                    <p className="text-sm text-muted-foreground">Compliant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-warning/20">
                    <AlertTriangle className="h-6 w-6 text-warning" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{counts.warning}</p>
                    <p className="text-sm text-muted-foreground">Warnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-destructive/20">
                    <XCircle className="h-6 w-6 text-destructive" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{counts.nonCompliant}</p>
                    <p className="text-sm text-muted-foreground">
                      Non-Compliant
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="relative w-16 h-16">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="text-muted stroke-current"
                        strokeWidth="10"
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                      />
                      <circle
                        className={`stroke-current ${overallScore >= 80 ? "text-success" : overallScore >= 60 ? "text-warning" : "text-destructive"}`}
                        strokeWidth="10"
                        strokeLinecap="round"
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        strokeDasharray={`${overallScore * 2.51} ${100 * 2.51}`}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold">{overallScore}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold">Overall Score</p>
                    <p className="text-sm text-muted-foreground">
                      {overallScore >= 80
                        ? "Good"
                        : overallScore >= 60
                          ? "Fair"
                          : "Needs Work"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Details */}
          <Tabs defaultValue="all" className="w-full">
            <TabsList>
              <TabsTrigger value="all">All Standards</TabsTrigger>
              <TabsTrigger value="issues">Issues Only</TabsTrigger>
              <TabsTrigger value="info">About FER</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-6 mt-6">
              {complianceCategories.map((category, catIdx) => (
                <Card key={catIdx}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <List className="h-5 w-5" />
                      {category.name}
                    </CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {category.checks.map(check => (
                        <div
                          key={check.id}
                          className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="mt-0.5">
                            {check.status === "compliant" && (
                              <CheckCircle className="h-5 w-5 text-success" />
                            )}
                            {check.status === "warning" && (
                              <AlertTriangle className="h-5 w-5 text-warning" />
                            )}
                            {check.status === "non_compliant" && (
                              <XCircle className="h-5 w-5 text-destructive" />
                            )}
                            {check.status === "not_applicable" && (
                              <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">{check.standard}</Badge>
                              <h4 className="font-medium">{check.title}</h4>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {check.description}
                            </p>
                            {check.details && (
                              <p className="text-sm mt-2">{check.details}</p>
                            )}
                            {check.recommendation && (
                              <div className="mt-2 p-2 bg-muted rounded text-sm flex items-start gap-2">
                                <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
                                <span>{check.recommendation}</span>
                              </div>
                            )}
                          </div>
                          <Badge
                            className={
                              check.status === "compliant"
                                ? "bg-success/20 text-success"
                                : check.status === "warning"
                                  ? "bg-warning/20 text-warning"
                                  : check.status === "non_compliant"
                                    ? "bg-destructive/20 text-destructive"
                                    : "bg-muted text-muted-foreground"
                            }
                          >
                            {check.status === "compliant" && "Compliant"}
                            {check.status === "warning" && "Warning"}
                            {check.status === "non_compliant" &&
                              "Non-Compliant"}
                            {check.status === "not_applicable" && "N/A"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="issues" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Items Requiring Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {complianceCategories
                      .flatMap(cat =>
                        cat.checks.filter(
                          c =>
                            c.status === "warning" ||
                            c.status === "non_compliant"
                        )
                      )
                      .map(check => (
                        <div
                          key={check.id}
                          className={`p-4 border rounded-lg ${check.status === "non_compliant" ? "border-destructive/30 bg-destructive/10" : "border-warning/30 bg-warning/10"}`}
                        >
                          <div className="flex items-start gap-4">
                            {check.status === "non_compliant" ? (
                              <XCircle className="h-5 w-5 text-destructive mt-0.5" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline">
                                  {check.standard}
                                </Badge>
                                <h4 className="font-medium">{check.title}</h4>
                              </div>
                              <p className="text-sm">{check.details}</p>
                              {check.recommendation && (
                                <div className="mt-2 p-2 bg-white dark:bg-muted rounded text-sm flex items-start gap-2">
                                  <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
                                  <span>{check.recommendation}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    {complianceCategories.flatMap(cat =>
                      cat.checks.filter(
                        c =>
                          c.status === "warning" || c.status === "non_compliant"
                      )
                    ).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                        <p>No compliance issues found!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="info" className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    About Swiss GAAP FER
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    Swiss GAAP FER (Fachempfehlungen zur Rechnungslegung) are
                    the accounting standards issued by the Foundation for
                    Accounting and Reporting Recommendations in Switzerland.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <h4 className="font-medium">
                        Who Must Apply Swiss GAAP FER?
                      </h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>
                          Companies listed on SIX Exchange Regulation (domestic
                          standard)
                        </li>
                        <li>Large cooperatives and foundations</li>
                        <li>
                          Companies that voluntarily adopt these standards
                        </li>
                        <li>Public institutions requiring transparency</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Key Benefits</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        <li>Tailored for Swiss business environment</li>
                        <li>Less complex than IFRS</li>
                        <li>Cost-effective implementation</li>
                        <li>Widely accepted by Swiss banks and investors</li>
                      </ul>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <Button variant="outline" asChild>
                      <a
                        href="https://www.fer.ch"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Visit Swiss GAAP FER Website
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}
